import { Context } from '@/context/appContext';
import { getResponseError } from '@/helpers';
import { useNotifications } from '@/hooks/useNotifications';
import { AppStateActions } from '@/reducers/appReducer';
import { Box, Link, TextField, useTheme } from '@mui/material';
import { requestRestoreUserPassword } from '@tailored-pdf/api';
import { useFormik } from 'formik';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LabeledField } from './LabeledField';
import { Header } from './styles';
import { LoginButton } from './LoginButton';

type LoginFormDataType = {
  email: string;
};

export function RestoreView() {
  const { t } = useTranslation('web', { keyPrefix: 'components.AuthModal' });
  const theme = useTheme();
  const { dispatch } = useContext(Context);
  const { notifyError, notifySuccess } = useNotifications();
  const [loading, setLoading] = useState(false);

  const handleBack = () =>
    dispatch({
      type: AppStateActions.SET_AUTH_STATE,
      payload: { state: 'SIGN_IN' },
    });

  const handleRestore = useCallback(
    async (data: LoginFormDataType) => {
      setLoading(true);
      try {
        await requestRestoreUserPassword(data);
        dispatch({ type: AppStateActions.SET_AUTH_STATE, payload: {} });
        notifySuccess(t('successRestoreLinkSend'));
      } catch (e: unknown) {
        notifyError(getResponseError(e));
      } finally {
        setLoading(false);
      }
    },
    [dispatch, notifyError, notifySuccess, t],
  );

  const loginForm = useFormik<LoginFormDataType>({
    initialValues: { email: '' },
    onSubmit: handleRestore,
  });

  return (
    <Box gap={4} display={'flex'} flexDirection={'column'} padding={8} width={{ xs: '100%', md: theme.spacing(110) }}>
      <Header>{t('restoreHeader')}</Header>
      <form onSubmit={loginForm.handleSubmit}>
        <Box gap={4} display={'flex'} flexDirection={'column'}>
          <LabeledField label={t('emailLabel')}>
            <TextField
              required
              name="email"
              type="email"
              placeholder={t('emailPlaceholder')}
              variant="outlined"
              size="small"
              value={loginForm.values.email}
              onChange={loginForm.handleChange}
            />
          </LabeledField>
          <LoginButton loading={loading}>{t('restorePassword')}</LoginButton>
        </Box>
      </form>
      <Box display={'flex'} gap={3} justifyContent={'center'} fontWeight={600} fontSize={14}>
        <span>{t('havePassword')}</span>
        <Link underline="none">
          <button
            onClick={handleBack}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              color: 'inherit',
              font: 'inherit',
              cursor: 'inherit',
            }}
          >
            {t('signIn')}
          </button>
        </Link>
      </Box>
    </Box>
  );
}
